import { useState } from "react";
import Layout from "@/components/Layout";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Palette, Copy, Sparkles } from "lucide-react";
import { toast } from "sonner";
import Markdown from "@/components/Markdown";
import ExportMenu from "@/components/ExportMenu";
import QuickActionBar from "@/components/QuickActionBar";

const KINDS = [
  { key: "notes", label: "Study Notes" },
  { key: "presentation", label: "Presentation outline" },
  { key: "study_guide", label: "Study guide" },
  { key: "poster", label: "Classroom poster" },
  { key: "infographic", label: "Infographic" },
];

const LANGS = ["English", "Hindi", "Kannada", "Tamil", "Telugu", "Marathi", "Bengali", "Arabic", "French", "Spanish"];

export default function ContentStudio() {
  const [form, setForm] = useState({
    kind: "notes",
    subject: "Mathematics",
    grade: "9",
    topic: "Quadratic Equations",
    audience: "students",
    language: "English",
  });
  const [busy, setBusy] = useState(false);
  const [output, setOutput] = useState(null);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    setBusy(true);
    setOutput(null);
    try {
      const { data } = await api.post("/content/generate", form);
      setOutput(data);
      toast.success("Generated!");
    } catch {
      toast.error("Generation failed");
    } finally {
      setBusy(false);
    }
  };

  const copy = async () => {
    if (!output) return;
    try {
      await navigator.clipboard.writeText(output.content);
      toast.success("Copied");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <Layout title="Content Studio">
      <QuickActionBar exclude={["ppt"]} />
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-5">
        <div className="lg:col-span-2 tb-card p-6 space-y-4" data-testid="content-studio-form">
          <div>
            <Label>Type</Label>
            <Select value={form.kind} onValueChange={(v) => update("kind", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl" data-testid="content-kind">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {KINDS.map((k) => (
                  <SelectItem key={k.key} value={k.key}>{k.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Topic</Label>
            <Input
              value={form.topic}
              onChange={(e) => update("topic", e.target.value)}
              className="mt-1.5 h-11 rounded-xl"
              data-testid="content-topic"
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Subject</Label>
              <Input
                value={form.subject}
                onChange={(e) => update("subject", e.target.value)}
                className="mt-1.5 h-11 rounded-xl"
                data-testid="content-subject"
              />
            </div>
            <div>
              <Label>Grade</Label>
              <Input
                value={form.grade}
                onChange={(e) => update("grade", e.target.value)}
                className="mt-1.5 h-11 rounded-xl"
                data-testid="content-grade"
              />
            </div>
          </div>
          <div>
            <Label>Audience (optional)</Label>
            <Input
              value={form.audience}
              onChange={(e) => update("audience", e.target.value)}
              placeholder="students, teachers, parents…"
              className="mt-1.5 h-11 rounded-xl"
            />
          </div>
          <div>
            <Label>Language</Label>
            <Select value={form.language} onValueChange={(v) => update("language", v)}>
              <SelectTrigger className="mt-1.5 h-11 rounded-xl">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {LANGS.map((l) => (
                  <SelectItem key={l} value={l}>{l}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            onClick={submit}
            disabled={busy}
            className="w-full h-11 rounded-full"
            data-testid="content-generate-button"
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Palette className="h-4 w-4 mr-1.5" /> Generate</>}
          </Button>
        </div>

        <div className="lg:col-span-3 tb-card p-6 min-h-[480px]" data-testid="content-output">
          {!output && !busy && (
            <div className="h-full grid place-items-center text-center text-muted-foreground py-20">
              <div>
                <Sparkles className="h-8 w-8 mx-auto opacity-50" />
                <p className="mt-3 font-display">Beautiful content will appear here.</p>
              </div>
            </div>
          )}
          {busy && (
            <div className="h-full grid place-items-center text-center text-muted-foreground py-20">
              <div>
                <Loader2 className="h-8 w-8 animate-spin mx-auto" />
                <p className="mt-3 font-display">Designing your content…</p>
              </div>
            </div>
          )}
          {output && (
            <>
              <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                <div>
                  <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted-foreground">{output.kind.replace(/_/g, " ")}</p>
                  <h3 className="mt-1 font-display text-xl tracking-tight font-medium">{output.title}</h3>
                </div>
                <div className="flex items-center gap-2">
                  <ExportMenu title={output.title} content={output.content} includePptx={true} />
                  <Button onClick={copy} variant="outline" size="sm" className="rounded-full">
                    <Copy className="h-3.5 w-3.5 mr-1.5" /> Copy
                  </Button>
                </div>
              </div>
              <Markdown>{output.content}</Markdown>
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}
